import { BusinessLine } from 'src/app/shared/models/businessLine-model';

/**
 * Convenzione di un'azienda B2B (ConventionDTO del backend, `/v1/conventions`).
 * Per ora è solo dimostrativa: nessuna logica di pagamento o di consumo delle ore.
 */
export interface Convention {
  id?: string;
  companyId: string;
  status: ConventionStatus;
  /** Date ISO `yyyy-MM-dd`. */
  validFrom?: string | null;
  validTo?: string | null;
  totalHours?: number | null;
  sharedHoursPool?: SharedHoursPool | null;
  rules?: ConventionRule[];
}

export type ConventionStatus = 'ACTIVE' | 'PASSIVE';

/** Monte ore condiviso tra più linee di business. */
export interface SharedHoursPool {
  enabled: boolean;
  hours?: number | null;
  businessLines: BusinessLine[];
}

/**
 * Prezzo di un prodotto dentro la convenzione (importi in centesimi, come `Price.amount`). I prodotti dei
 * servizi B2B non hanno prezzi di listino:
 * - a prestazione (SINGLE): un costo fisso in `minPrice`, `maxPrice` vuoto;
 * - orario (HOURLY): un intervallo `minPrice`–`maxPrice`, la tariffa esatta si definisce sulla richiesta.
 */
export interface ConventionRule {
  productId: number;
  pricingMode: PricingMode;
  minPrice?: number | null;
  maxPrice?: number | null;
  /** Selezioni/sedute (SINGLE) o ore (HOURLY) comprese nella convenzione. */
  quantity?: number | null;
}

export type PricingMode = 'SINGLE' | 'HOURLY';

export const PRICING_MODES: PricingMode[] = ['SINGLE', 'HOURLY'];

export interface PricingUnit {
  label: string;
  unit: string;
  units: string;
}

/**
 * Come si chiama l'unità di prezzo: a ore, oppure a prestazione, che nel caregiving è la "selezione"
 * (del caregiver) e negli altri servizi la "seduta".
 */
export function pricingUnitOf(mode: PricingMode | string, caregiving: boolean): PricingUnit {
  if (mode === 'HOURLY') return { label: 'Orario', unit: 'ora', units: 'ore' };
  return caregiving
    ? { label: 'A selezione', unit: 'selezione', units: 'selezioni' }
    : { label: 'A seduta', unit: 'seduta', units: 'sedute' };
}

export const STATUS_LABELS: Record<ConventionStatus, string> = {
  ACTIVE: 'Attiva',
  PASSIVE: 'Non attiva',
};

/** Servizi con prezzo definito sulla richiesta (regole della convenzione) invece che dal listino. */
export function isB2BService(service: { customerType?: string } | null | undefined): boolean {
  return service?.customerType === 'B2B';
}

/** La convenzione da mostrare tra quelle dell'azienda: la prima attiva, altrimenti la prima. */
export function pickConvention(list: Convention[] | null | undefined): Convention | null {
  if (!list?.length) return null;
  return list.find((c) => c.status === 'ACTIVE') ?? list[0];
}

export function ruleFor(convention: Convention | null, productId: number): ConventionRule | null {
  return convention?.rules?.find((r) => r.productId === productId) ?? null;
}

/** Sostituisce (o aggiunge) la regola dello stesso prodotto. */
export function withRule(convention: Convention, rule: ConventionRule): Convention {
  const rules = (convention.rules ?? []).filter((r) => r.productId !== rule.productId);
  return { ...convention, rules: [...rules, rule] };
}

export function withoutRule(convention: Convention, productId: number): Convention {
  return { ...convention, rules: (convention.rules ?? []).filter((r) => r.productId !== productId) };
}

const fmtEuro = (cents: number) =>
  (cents / 100).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Costo zero: gratuito per il dipendente (lo copre la convenzione). */
export function isFreeRule(rule: ConventionRule): boolean {
  const min = rule.minPrice ?? null;
  const max = rule.maxPrice ?? null;
  if (min === null && max === null) return false;
  return rule.pricingMode === 'HOURLY' ? (min ?? 0) === 0 && (max ?? 0) === 0 : (min ?? max) === 0;
}

/**
 * "50,00 € a selezione" / "50,00 € a seduta" (costo fisso), "12,00–16,00 €/ora" o "12,00 €/ora" (tariffa
 * oraria), "fino a 14,00 €/ora" con la minima a zero, "Gratuito" a costo zero, "Prezzo da definire" senza importi.
 */
export function rulePriceText(rule: ConventionRule, caregiving: boolean): string {
  if (isFreeRule(rule)) return 'Gratuito';
  const min = rule.minPrice || null;
  const max = rule.maxPrice ?? null;
  if (rule.pricingMode !== 'HOURLY') {
    const fixed = min ?? max;
    return fixed === null ? 'Prezzo da definire' : `${fmtEuro(fixed)} € a ${pricingUnitOf('SINGLE', caregiving).unit}`;
  }
  if (min === null && max === null) return 'Prezzo da definire';
  if (min !== null && max !== null) return min === max ? `${fmtEuro(min)} €/ora` : `${fmtEuro(min)}–${fmtEuro(max)} €/ora`;
  return min !== null ? `da ${fmtEuro(min)} €/ora` : `fino a ${fmtEuro(max!)} €/ora`;
}

/** "2 selezioni gratuite" a costo zero, "2 selezioni scontate" con un prezzo, "10 ore scontate"; null senza quantità. */
export function ruleQuantityText(rule: ConventionRule, caregiving: boolean): string | null {
  if (!rule.quantity) return null;
  const unit = pricingUnitOf(rule.pricingMode, caregiving);
  const one = rule.quantity === 1;
  const priced = (rule.minPrice ?? null) !== null || (rule.maxPrice ?? null) !== null;
  const [single, plural] = !priced ? ['inclusa', 'incluse'] : isFreeRule(rule) ? ['gratuita', 'gratuite'] : ['scontata', 'scontate'];
  return `${rule.quantity} ${one ? unit.unit : unit.units} ${one ? single : plural}`;
}

/** "1 gen 2026 – 31 dic 2026", "dal 1 gen 2026", ecc. */
export function validityText(convention: Convention): string {
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  const { validFrom, validTo } = convention;
  if (validFrom && validTo) return `${fmt(validFrom)} – ${fmt(validTo)}`;
  if (validFrom) return `dal ${fmt(validFrom)}`;
  if (validTo) return `fino al ${fmt(validTo)}`;
  return 'Senza scadenza';
}
